import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Connects to data-controller="access-report"
export default class extends Controller {
  static values = {
    partnerSlug: String,
    zip: String,
    radius: String,
    prospect: String
  }
  static targets = [
    "prospectDisplay", "prospectInput", "prospectText",
    "heroPreparedFor",
    "searchContainer", "searchInput", "searchDropdown",
    "tableBody", "loadMoreContainer", "loadMoreBtn", "loadMoreRow",
    "providerRow", "showingCount", "donutChart"
  ]

  connect() {
    this.searchTimeout = null
    this.highlightedNpi = null

    // Initialize donut charts
    this.initDonutCharts()

    // Close search dropdown on outside click
    this.boundOnOutsideClick = (e) => {
      if (this.hasSearchContainerTarget && !this.searchContainerTarget.contains(e.target)) {
        this.hideSearchDropdown()
      }
    }
    document.addEventListener("click", this.boundOnOutsideClick)

    // Listen for map provider clicks — filter table to that provider
    this.boundOnMapProviderClick = (e) => {
      this.filterTableToProvider(e.detail.npi)
    }
    document.addEventListener("access-report-map:providerClick", this.boundOnMapProviderClick)

    // Update hero stats and initialize donut charts when summary frame loads
    this.boundOnFrameLoad = (e) => {
      if (e.target.id === "ar-summary") {
        const tpl = e.target.querySelector("template[data-hero-stats]")
        const el = document.getElementById("ar-hero-stats")
        if (tpl && el) el.textContent = tpl.innerHTML.trim()
        this.initDonutCharts()
      }
      if (e.target.id === "ar-provider-directory") {
        this.filteredNpi = null
        this.originalShowingCount = null
        this.removeClearFilterButton()

        // Detect active type filter from chips and notify map
        const activeChip = e.target.querySelector("a[data-type-filter].no-underline")
        if (activeChip) {
          // Find which chip is active by checking for the active chip color class
          const allChips = e.target.querySelectorAll("a[data-type-filter]")
          allChips.forEach(chip => {
            const inner = chip.querySelector("div, span")
            if (inner && inner.classList.contains("bg-core-purple-200")) {
              const typeFilter = chip.dataset.typeFilter
              document.dispatchEvent(new CustomEvent("access-report:typeFilterChanged", {
                detail: { type: typeFilter }
              }))
            }
          })
        }
      }
    }
    document.addEventListener("turbo:frame-load", this.boundOnFrameLoad)
  }

  disconnect() {
    document.removeEventListener("click", this.boundOnOutsideClick)
    document.removeEventListener("access-report-map:providerClick", this.boundOnMapProviderClick)
    document.removeEventListener("turbo:frame-load", this.boundOnFrameLoad)
  }

  // ── Donut Charts ───────────────────────────────────
  initDonutCharts() {
    if (!this.hasDonutChartTarget) return
    if (typeof ApexCharts === "undefined") return

    this.donutChartTargets.forEach(el => {
      const green = parseInt(el.dataset.green) || 0
      const yellow = parseInt(el.dataset.yellow) || 0
      const red = parseInt(el.dataset.red) || 0
      const total = green + yellow + red
      const centerText = el.dataset.centerText || "0%"

      if (total === 0) {
        el.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:12px;color:var(--color-text-light);">No data</div>'
        return
      }

      const chart = new ApexCharts(el, {
        chart: {
          type: "donut",
          width: 160,
          height: 160,
          sparkline: { enabled: false },
          animations: { enabled: true }
        },
        series: [green, yellow, red],
        colors: ["#4ADB54", "#E08C4C", "#E04C4C"],
        labels: ["Low risk", "Moderate risk", "High risk"],
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                name: { show: false },
                value: { show: false },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Low risk",
                  fontSize: "20px",
                  fontWeight: 700,
                  fontFamily: "var(--f-sans)",
                  color: "#1E2046",
                  formatter: () => centerText
                }
              }
            }
          }
        },
        legend: { show: false },
        tooltip: { enabled: true },
        dataLabels: { enabled: false },
        stroke: { width: 2, colors: ["#fff"] },
        states: {
          hover: { filter: { type: "none" } },
          active: { filter: { type: "none" } }
        }
      })
      chart.render()
    })
  }

  // ── Radius Selector ────────────────────────────────
  changeRadius(e) {
    const form = e.target.closest("form")
    if (form) form.requestSubmit()
  }

  // ── Prospect Name ──────────────────────────────────
  editProspectName() {
    if (!this.hasProspectInputTarget || !this.hasProspectDisplayTarget) return
    this.prospectDisplayTarget.classList.add("hidden")
    this.prospectInputTarget.classList.remove("hidden")
    this.prospectInputTarget.focus()
  }

  onProspectInput() {
    const name = this.prospectInputTarget.value.trim()

    // Update prospect text display in real-time
    if (this.hasProspectTextTarget) {
      this.prospectTextTarget.textContent = name || "Add prospect name..."
      this.prospectTextTarget.style.color = name ? "var(--color-text)" : "var(--color-text-light)"
    }

    // Update hero "Prepared for" in real-time
    if (this.hasHeroPreparedForTarget) {
      this.heroPreparedForTarget.textContent = name ? `Prepared for ${name}` : ""
      this.heroPreparedForTarget.classList.toggle("hidden", !name)
    }

  }

  saveProspectName() {
    if (!this.hasProspectInputTarget || !this.hasProspectDisplayTarget) return
    const name = this.prospectInputTarget.value.trim()
    this.prospectDisplayTarget.classList.remove("hidden")
    this.prospectInputTarget.classList.add("hidden")

    if (this.hasProspectTextTarget) {
      this.prospectTextTarget.textContent = name || "Add prospect name..."
      this.prospectTextTarget.style.color = name ? "var(--color-text)" : "var(--color-text-light)"
    }

    // Update URL query param
    const url = new URL(window.location.href)
    if (name) {
      url.searchParams.set("prospect", name)
    } else {
      url.searchParams.delete("prospect")
    }
    window.history.replaceState({}, "", url.toString())

    // Sync hidden prospect field in zip form so name survives form submission
    const zipForm = this.element.querySelector("form")
    if (zipForm) {
      let hidden = zipForm.querySelector('input[name="prospect"]')
      if (name) {
        if (!hidden) {
          hidden = document.createElement("input")
          hidden.type = "hidden"
          hidden.name = "prospect"
          zipForm.prepend(hidden)
        }
        hidden.value = name
      } else if (hidden) {
        hidden.remove()
      }
    }

  }

  prospectKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault()
      this.saveProspectName()
    }
  }

  // ── Provider Search ────────────────────────────────
  onSearchInput() {
    clearTimeout(this.searchTimeout)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 2) {
      this.hideSearchDropdown()
      return
    }

    this.searchTimeout = setTimeout(() => this.fetchSearchResults(query), 150)
  }

  onSearchFocus() {
    const query = this.searchInputTarget.value.trim()
    if (query.length >= 2) {
      this.fetchSearchResults(query)
    }
  }

  async fetchSearchResults(query) {
    try {
      let url = `/partners/${this.partnerSlugValue}/access_report/provider_search?q=${encodeURIComponent(query)}&zip=${this.zipValue}`
      if (this.radiusValue) url += `&radius=${this.radiusValue}`
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      const html = await response.text()
      this.renderSearchResults(html)
    } catch (e) {
      console.error("Search failed:", e)
    }
  }

  renderSearchResults(html) {
    if (!this.hasSearchDropdownTarget) return

    if (!html || html.trim().length === 0) {
      this.hideSearchDropdown()
      return
    }

    this.searchDropdownTarget.innerHTML = html
    this.searchDropdownTarget.classList.remove("hidden")
  }

  selectSearchResult(e) {
    const resultEl = e.currentTarget
    const npi = resultEl.dataset.npi
    const lat = parseFloat(resultEl.dataset.lat)
    const lng = parseFloat(resultEl.dataset.lng)

    this.hideSearchDropdown()
    this.searchInputTarget.value = ""

    // Filter table to only show the selected provider
    this.filterTableToProvider(npi)

    // Dispatch event for map to highlight + pan
    this.dispatch("providerSelected", { detail: { npi, lat, lng } })
  }

  filterTableToProvider(npi) {
    // Guard: if the provider row isn't in the DOM, fall back to highlight behavior
    const matchedRow = this.element.querySelector(`tr[data-npi="${npi}"]`)
    if (!matchedRow) {
      this.highlightProviderInTable(npi)
      return
    }

    this.filteredNpi = npi

    // Hide all rows that don't match
    this.element.querySelectorAll("tr[data-npi]").forEach(row => {
      const detail = this.element.querySelector(`tr[data-npi-detail="${row.dataset.npi}"]`)
      if (row.dataset.npi === npi) {
        row.classList.remove("hidden")
        row.classList.add("highlighted")
        if (detail) {
          detail.classList.remove("hidden")
          detail.classList.add("highlighted")
          row.classList.add("expanded")
          const chevron = row.querySelector("[data-chevron]")
          if (chevron) chevron.style.transform = "rotate(90deg)"
        }
      } else {
        row.classList.add("hidden")
        if (detail) detail.classList.add("hidden")
      }
    })

    // Hide load-more button while filtered
    if (this.hasLoadMoreContainerTarget) this.loadMoreContainerTarget.classList.add("hidden")
    if (this.hasLoadMoreRowTarget) this.loadMoreRowTarget.classList.add("hidden")

    // Update showing count
    if (this.hasShowingCountTarget) {
      this.originalShowingCount = this.originalShowingCount || this.showingCountTarget.textContent
      this.showingCountTarget.textContent = "Showing 1 provider (filtered) · "
    }

    // Show clear-filter button
    this.showClearFilterButton()

    // Scroll to the matched row
    matchedRow.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  clearProviderFilter() {
    this.filteredNpi = null

    // Show all rows again
    this.element.querySelectorAll("tr[data-npi]").forEach(row => {
      row.classList.remove("hidden", "highlighted")
      const detail = this.element.querySelector(`tr[data-npi-detail="${row.dataset.npi}"]`)
      if (detail) {
        detail.classList.add("hidden")
        detail.classList.remove("highlighted")
        row.classList.remove("expanded")
        const chevron = row.querySelector("[data-chevron]")
        if (chevron) chevron.style.transform = ""
      }
    })

    // Restore load-more button
    if (this.hasLoadMoreContainerTarget) this.loadMoreContainerTarget.classList.remove("hidden")
    if (this.hasLoadMoreRowTarget) this.loadMoreRowTarget.classList.remove("hidden")

    // Restore showing count
    if (this.hasShowingCountTarget && this.originalShowingCount) {
      this.showingCountTarget.textContent = this.originalShowingCount
      this.originalShowingCount = null
    }

    // Remove clear-filter button
    this.removeClearFilterButton()
  }

  showClearFilterButton() {
    this.removeClearFilterButton()
    const btn = document.createElement("button")
    btn.id = "ar-clear-filter-btn"
    btn.textContent = "✕ Clear filter"
    btn.style.cssText = "font-size: 12px; font-weight: 600; padding: 4px 12px; border-radius: 8px; border: 1px solid var(--color-border); background: var(--color-white); color: var(--color-primary); cursor: pointer;"
    btn.addEventListener("click", () => this.clearProviderFilter())

    const slot = document.getElementById("ar-clear-filter-slot")
    if (slot) {
      slot.appendChild(btn)
    }
  }

  removeClearFilterButton() {
    const existing = document.getElementById("ar-clear-filter-btn")
    if (existing) existing.remove()
  }

  hideSearchDropdown() {
    if (this.hasSearchDropdownTarget) {
      this.searchDropdownTarget.classList.add("hidden")
    }
  }

  // ── Table Row Expand/Collapse ──────────────────────
  toggleRow(e) {
    const row = e.currentTarget
    const npi = row.dataset.npi
    if (!npi) return

    const detailRow = this.element.querySelector(`tr[data-npi-detail="${npi}"]`)
    if (!detailRow) return

    const isExpanded = !detailRow.classList.contains("hidden")
    if (isExpanded) {
      detailRow.classList.add("hidden")
      row.classList.remove("expanded")
      const chevron = row.querySelector("[data-chevron]")
      if (chevron) chevron.style.transform = ""
    } else {
      detailRow.classList.remove("hidden")
      row.classList.add("expanded")
      const chevron = row.querySelector("[data-chevron]")
      if (chevron) chevron.style.transform = "rotate(90deg)"
    }
  }

  highlightProviderInTable(npi) {
    // Skip highlight if a provider filter is active to avoid orphaned detail rows
    if (this.filteredNpi) return

    // Remove previous highlight
    this.element.querySelectorAll("tr.highlighted").forEach(r => r.classList.remove("highlighted"))

    const row = this.element.querySelector(`tr[data-npi="${npi}"]`)
    const detailRow = this.element.querySelector(`tr[data-npi-detail="${npi}"]`)

    if (row) {
      row.classList.add("highlighted")
      // Expand the detail row
      if (detailRow) {
        detailRow.classList.remove("hidden")
        detailRow.classList.add("highlighted")
        row.classList.add("expanded")
        const chevron = row.querySelector("[data-chevron]")
        if (chevron) chevron.style.transform = "rotate(90deg)"
      }
      row.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  // ── Load More Pagination ───────────────────────────
  async loadMore(e) {
    const btn = e.currentTarget
    const url = btn.dataset.url
    if (!url) return

    btn.disabled = true
    btn.textContent = "Loading..."

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`)
      }

      const html = await response.text()

      // Remove load more container/row
      if (this.hasLoadMoreContainerTarget) {
        this.loadMoreContainerTarget.remove()
      }
      if (this.hasLoadMoreRowTarget) {
        this.loadMoreRowTarget.remove()
      }

      // Append new rows
      if (this.hasTableBodyTarget) {
        this.tableBodyTarget.insertAdjacentHTML("beforeend", html)
      }
    } catch (err) {
      console.error("Load more failed:", err)
      btn.disabled = false
      btn.textContent = "Retry"
    }
  }

  // ── Utility ────────────────────────────────────────
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
